import { ArrowLeft, Cloud, FolderInput, Info, Star } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GlobalFile } from '../../../api/types';
import googleSheetsIntegrationApi from '../../../api/googleSheetsIntegrationApi';
import { buildGoogleSheetFullEditorUrl } from '../utils/googleSheetsEmbed';
import { displayWorkspaceFolderPathForFile } from '../utils/workspacePaths';
import SheetContextPanel from './SheetContextPanel';

/** Google’s iframe cannot tell Finz when cells change; we re-import on a timer + when you return to this tab. */
const AUTO_RESYNC_INTERVAL_MS = 90_000;
const MIN_MS_BETWEEN_SYNCS = 45_000;

interface GoogleSheetWorkspacePanelProps {
    file: GlobalFile;
    onClose: () => void;
    /** After Mongo/Pinecone update from re-sync (optional list refresh). */
    onAfterAutoResync?: () => void;
}

/**
 * Full main-area layout: breadcrumb + title row + Google Sheets editor iframe (full UI chrome).
 * Matches Finz “files + embedded Sheets + AI CFO” layout beside the chat panel.
 */
export default function GoogleSheetWorkspacePanel({
    file,
    onClose,
    onAfterAutoResync,
}: GoogleSheetWorkspacePanelProps) {
    const sheetUrl = file.external_document?.spreadsheet_url?.trim();
    const editorSrc = buildGoogleSheetFullEditorUrl(sheetUrl);
    const folderPath = displayWorkspaceFolderPathForFile(file) || file.workspace_folder_path?.trim();

    const [syncHint, setSyncHint] = useState<string | null>(null);
    const [contextOpen, setContextOpen] = useState(false);

    const sourceIdRef = useRef(file.google_sheet_source_document_id);
    const inFlightRef = useRef(false);
    const lastCompletedAtRef = useRef<number>(0);

    sourceIdRef.current = file.google_sheet_source_document_id;

    const runAutoResync = useCallback(
        async (reason: 'interval' | 'visible') => {
            const sid = sourceIdRef.current?.trim();
            if (!sid) return;
            const now = Date.now();
            if (now - lastCompletedAtRef.current < MIN_MS_BETWEEN_SYNCS && lastCompletedAtRef.current > 0) {
                return;
            }
            if (inFlightRef.current) return;
            inFlightRef.current = true;
            setSyncHint(reason === 'visible' ? 'Syncing after you came back…' : 'Saving copy to Finz…');
            try {
                await googleSheetsIntegrationApi.resync(sid);
                lastCompletedAtRef.current = Date.now();
                setSyncHint(`Finz data updated ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
                onAfterAutoResync?.();
            } catch {
                setSyncHint('Could not sync to Finz (try Re-sync in details)');
            } finally {
                inFlightRef.current = false;
            }
        },
        [onAfterAutoResync],
    );

    // First backup ~45s after open, then on a fixed interval (iframe cannot emit edit events).
    useEffect(() => {
        const sid = sourceIdRef.current?.trim();
        if (!sid) return undefined;
        const first = window.setTimeout(() => void runAutoResync('interval'), 45_000);
        const id = window.setInterval(() => void runAutoResync('interval'), AUTO_RESYNC_INTERVAL_MS);
        return () => {
            window.clearTimeout(first);
            window.clearInterval(id);
        };
    }, [file.file_id, runAutoResync]);

    // When you switch back to Finz after editing in Google elsewhere, pull latest.
    useEffect(() => {
        const sid = sourceIdRef.current?.trim();
        if (!sid) return undefined;

        const onVis = () => {
            if (document.visibilityState !== 'visible') return;
            void runAutoResync('visible');
        };

        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [file.file_id, runAutoResync]);

    // Deep-link: the read-path clarification card in chat dispatches
    // ``finz:open-sheet-context`` with the source doc id when the user
    // clicks "Open context". We respond by expanding the rail so they
    // can inspect/correct the schema without leaving the workspace.
    useEffect(() => {
        const sid = file.google_sheet_source_document_id;
        if (!sid) return undefined;
        const onOpen = (evt: Event) => {
            const detail = (evt as CustomEvent<{ sourceDocumentId?: string }>).detail;
            if (detail?.sourceDocumentId && detail.sourceDocumentId !== sid) return;
            setContextOpen(true);
        };
        window.addEventListener('finz:open-sheet-context', onOpen as EventListener);
        return () => window.removeEventListener('finz:open-sheet-context', onOpen as EventListener);
    }, [file.google_sheet_source_document_id]);

    const openInGoogle = () => {
        if (sheetUrl) window.open(sheetUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="flex flex-col flex-1 min-h-0 -mx-5 -mt-1">
            <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-2">
                <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                >
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    Back to Files
                </button>

                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0 text-xs text-gray-500">
                        <span className="text-gray-400">All folders</span>
                        {folderPath ? (
                            <>
                                <span className="mx-1.5 text-gray-300">/</span>
                                <span className="text-gray-600">{folderPath}</span>
                            </>
                        ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        {file.google_sheet_source_document_id && syncHint ? (
                            <p className="shrink-0 text-xs text-gray-500">
                                {syncHint}
                            </p>
                        ) : null}
                        <div className="flex shrink-0 items-center gap-1">
                        {file.google_sheet_source_document_id ? (
                            <button
                                type="button"
                                title={contextOpen ? 'Hide sheet context' : 'Show sheet context'}
                                onClick={() => setContextOpen((prev) => !prev)}
                                className={`rounded-lg p-2 transition ${
                                    contextOpen
                                        ? 'bg-gray-100 text-gray-800'
                                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                                }`}
                                aria-label="Toggle sheet context panel"
                                aria-pressed={contextOpen}
                            >
                                <Info className="h-4 w-4" />
                            </button>
                        ) : null}
                        <button
                            type="button"
                            title="Google Sheets — star & file actions open in a new tab"
                            onClick={openInGoogle}
                            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                            aria-label="Open in Google Sheets"
                        >
                            <Star className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            title="Move in Google Drive"
                            onClick={openInGoogle}
                            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                            aria-label="Folder in Google Sheets"
                        >
                            <FolderInput className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            title="Sync status in Google"
                            onClick={openInGoogle}
                            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                            aria-label="Cloud sync in Google Sheets"
                        >
                            <Cloud className="h-4 w-4" />
                        </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-1 min-h-0 flex-row bg-[#f1f3f4]">
                <div className="flex min-h-0 flex-1 flex-col">
                    {editorSrc ? (
                        <iframe
                            title={file.original_filename || 'Google Sheet'}
                            src={editorSrc}
                            className="h-full w-full min-h-[480px] flex-1 border-0 bg-white"
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                    ) : (
                        <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-gray-600">
                            This sheet does not have a valid Google Spreadsheet URL. Open details or re-import the file.
                        </div>
                    )}
                </div>
                {file.google_sheet_source_document_id ? (
                    <SheetContextPanel
                        sourceDocumentId={file.google_sheet_source_document_id}
                        open={contextOpen}
                        onOpenChange={setContextOpen}
                    />
                ) : null}
            </div>
        </div>
    );
}
